import { GridConfig } from "./GridConfig";
import { ConnectingLineHandler } from "./ConnectingLineHandler";
import { getInstanceIDFromCanvasInstance } from "../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description This Class handles the creation of the diagram object model when is requested through the API
 */
export class ViewDataRetrieval {
    config;

    connectingLineHandler;

    cellIndependentPluginHandler;

    grid;

    camera;

    labelHandler;

    anchoredPluginsHandler;

    instanceId;

    constructor(grid, camera, cellIndependentPluginHandler, anchoredPluginsHandler, labelHandler, ctx) {
        this.grid = grid;
        this.camera = camera;
        this.instanceId = getInstanceIDFromCanvasInstance(ctx);
        this.config = new GridConfig(this.instanceId);
        this.connectingLineHandler = new ConnectingLineHandler(this.instanceId);
        this.labelHandler = labelHandler;
        this.anchoredPluginsHandler = anchoredPluginsHandler;
        this.cellIndependentPluginHandler = cellIndependentPluginHandler;
    }

    /**
     * @description The method used to return the diagram data object model
     */
    getData() {
        if (!this.grid || (this.grid && !this.grid[0])) return {};
        const worldData = {
            width: this.camera.WIDTH,
            height: this.camera.HEIGHT
        };
        const gridView = {
            padding: this.config.viewPadding,
            cellWidth: this.config.cellWidth,
            cellHeight: this.config.cellHeight,
            rows: this.grid.length,
            cols: this.grid[0] ? this.grid[0].length : 0
        };
        const content = this.getDiagramContentData();
        const connectors = this.getDiagramConnectorsData();
        return {
            worldData, gridView, content, connectors
        };
    }

    /**
     * @description gathers all plugin data
     */
    getDiagramContentData() {
        const value = [];
        const cellPlugins = this.getCellBasedPlugins();
        const cellIndependentPlugins = this.getCellIndependentPlugins();
        value.push(...cellPlugins);
        value.push(...cellIndependentPlugins);
        return value;
    }

    /**
    * @description gathers all cell independent plugin data
    */
    getCellIndependentPlugins() {
        const pluginKeys = this.cellIndependentPluginHandler.plugins.keys();
        const value = [];
        for (const key of pluginKeys) {
            const plugin = this.cellIndependentPluginHandler.plugins.get(key);
            const children = plugin.instance.getChildrenData();
            children.forEach((child) => {
                child.hasOwnState = true;
            });
            value.push({
                hasOwnState: true,
                uuid: plugin.instance.uuid,
                id: plugin.instance.data.id,
                x: plugin.instance.position.x,
                y: plugin.instance.position.y,
                width: plugin.instance.size.width,
                height: plugin.instance.size.height,
                data: plugin.instance.getPluginData(),
                children
            });
        }
        return value;
    }

    /**
   * @description gathers all connectors data
   */
    getDiagramConnectorsData() {
        const value = [];
        const connectorKeys = this.connectingLineHandler.connectors.keys();
        for (const key of connectorKeys) {
            const connector = this.connectingLineHandler.connectors.get(key);
            const fromCell = this.connectingLineHandler.cells.get(connector.from);
            const fromCellContent = fromCell.ref.getContentInstance();
            const toCell = this.connectingLineHandler.cells.get(connector.to);
            const toCellContent = toCell.ref.getContentInstance();
            const labelData = this.labelHandler.returnConnectorLabelOnSave(connector.instance.uuid);
            if (toCellContent) {
                value.push({
                    uuid: connector.instance.uuid,
                    from: fromCellContent.uuid,
                    to: toCellContent.uuid,
                    endSide: connector.instance.layout.endSide,
                    connectorCode: connector.connectorCode,
                    path: connector.instance.layout.path,
                    label: {
                        value: labelData.value,
                        uuid: labelData.uuid,
                        x: labelData.x,
                        y: labelData.y,
                        labelOf: connector.instance.uuid
                    }
                });
            }            
        }
        return value;
    }

    /**
   * @description gathers all cell based plugin data
   */
    getCellBasedPlugins() {
        const cellKeys = this.connectingLineHandler.cells.keys();
        const value = [];
        for (const key of cellKeys) {
            const cell = this.connectingLineHandler.cells.get(key);
            const cellContent = cell.ref.getContentInstance();
            if (cellContent) {
                const { layout } = cell.ref;
                const { data } = cellContent;
                const labelData = this.labelHandler.returnPluginLabelOnSave(cell.ref.content.instance.content.uuid);
                value.push({
                    uuid: cellContent.uuid,
                    id: data.id,
                    x: layout.x,
                    y: layout.y,
                    width: layout.width,
                    height: layout.height,
                    label: labelData,
                    anchoredPlugins: this.getAnchoredPlugins(cellContent.uuid),
                    outgoing: [...cell.outgoing],
                    incoming: [...cell.incoming]
                });
            }
        }
        return value;
    }

    /**
       * @description gathers all anchored plugins data
       */
    getAnchoredPlugins(key) {
        const plugin = this.anchoredPluginsHandler.plugins.get(key);
        if (plugin && plugin.children) {
            return Object.keys(plugin.children).map(childUuid => ({
                anchoredTo: key,
                anchoredElementUuid: childUuid,
                data: plugin.children[childUuid].instance.data
            }));
        }
        return [];
    }
}
